import { addressOf, telLink, type Church, type ClockTime, type Service, type ServiceCategory } from "@viamissa/masstimes";
import type { Event } from "@viamissa/schedule";
import { clock, WEEKDAY_SHORT } from "../lib/format";

const CATEGORY_LABEL: Record<ServiceCategory, string> = {
  mass: "Mass",
  confession: "Confession",
  adoration: "Adoration",
  devotion: "Devotions",
  other: "Other",
};

const ORDER: ServiceCategory[] = ["mass", "confession", "adoration", "devotion", "other"];

function clockText(time: ClockTime): string {
  const hour12 = time.hour % 12 === 0 ? 12 : time.hour % 12;
  return `${hour12}:${String(time.minute).padStart(2, "0")} ${time.hour < 12 ? "AM" : "PM"}`;
}

function serviceText(service: Service): string {
  if (service.perpetual) return "Perpetual";
  if (!service.start) return service.comment ?? service.label;
  const range = service.end ? `${clockText(service.start)}–${clockText(service.end)}` : clockText(service.start);
  const language = service.language && service.language !== "English" ? ` (${service.language})` : "";
  return range + language;
}

const dayLabel = (service: Service): string => {
  if (service.kind === "holyday") return "Holy days";
  if (service.kind === "holyday-vigil") return "Eve of holy days";
  return service.weekday === null ? "Any day" : WEEKDAY_SHORT[service.weekday]!;
};

/** A compact weekly schedule: one row per category, grouped by day. */
export function Schedule({ church }: { church: Church }) {
  const rows = ORDER.map((category) => {
    const services = church.services.filter((service) => service.category === category);
    if (!services.length) return null;
    const byDay = new Map<string, Service[]>();
    const sorted = [...services].sort(
      (a, b) => (a.weekday ?? 7) - (b.weekday ?? 7) || (a.start?.hour ?? 0) * 60 + (a.start?.minute ?? 0) - ((b.start?.hour ?? 0) * 60 + (b.start?.minute ?? 0)),
    );
    for (const service of sorted) {
      const key = dayLabel(service);
      byDay.set(key, [...(byDay.get(key) ?? []), service]);
    }
    return (
      <div className="schedule-row" key={category}>
        <dt>{CATEGORY_LABEL[category]}</dt>
        <dd>
          {[...byDay.entries()].map(([day, list]) => (
            <span className="schedule-day" key={day}>
              <b>{day}</b> {list.map(serviceText).join(", ")}
            </span>
          ))}
        </dd>
      </div>
    );
  });
  if (rows.every((row) => row === null)) return <p className="muted">No schedule listed.</p>;
  return <dl className="schedule">{rows}</dl>;
}

type Props = {
  church: Church;
  timeZone: string;
  homeZone: string;
  /** Services happening near when the traveller passes, to call out. */
  nearby?: Event[];
  children?: React.ReactNode;
};

export function ChurchCard({ church, timeZone, homeZone, nearby = [], children }: Props) {
  return (
    <article className="church">
      <header>
        <h3>{church.name}</h3>
        <p className="muted">{addressOf(church)}</p>
      </header>
      {children}
      <div className="actions">
        {church.phone && (
          <a className="button small" href={telLink(church.phone)}>
            Call {church.phone}
          </a>
        )}
        {church.website && (
          <a className="button small ghost" href={church.website} target="_blank" rel="noopener noreferrer">
            Website
          </a>
        )}
        <a
          className="button small ghost"
          href={`https://www.google.com/maps/search/?api=1&query=${church.lat},${church.lng}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Map
        </a>
      </div>
      {nearby.length > 0 && (
        <p className="nearby">
          Near when you pass:{" "}
          {nearby.map((event) => `${event.category === "mass" ? "Mass" : event.category === "confession" ? "Confession" : event.category === "adoration" ? "Adoration" : "Devotion"} ${clock(event.start, timeZone, homeZone)}`).join(" · ")}
        </p>
      )}
      <details>
        <summary>Weekly schedule</summary>
        <Schedule church={church} />
        <p className="fine">
          {church.wheelchairAccess ? "Wheelchair accessible. " : ""}
          {church.lastUpdated ? `Listing updated ${church.lastUpdated.split(" ")[0]}. ` : ""}
          Schedules change: call ahead before you count on a time.
        </p>
      </details>
    </article>
  );
}
